import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { CurrentUser } from "../lib/types";
import { QrCode } from "../components/QrCode";
import { Badge, Button, Card, ErrorNote, Field, Input, PageHeader } from "../components/ui";

interface Enrolment {
  secret: string;
  otpauthUrl: string;
}

/**
 * Everything here is about the account rather than the business, which is why
 * it sits apart from the sections in the sidebar.
 */
export function Settings() {
  const { user } = useAuth();
  if (user === null) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        lede="Your account. Nothing here affects anyone else's."
      />
      <YourDetails user={user} />
      <ChangePassword />
      <TwoFactor user={user} />
    </div>
  );
}

/**
 * What the account is, read-only.
 *
 * The address is deliberately not editable here. For a marketer or a customer
 * it is also the sign-in for a commercial record — `Marketer.email` and the
 * login must move together or the two drift, which is exactly the bug the
 * admin-side marketer editor exists to prevent. Changing it is therefore an
 * admin action with both sides in view, not a self-service field with one.
 */
function YourDetails({ user }: { user: CurrentUser }) {
  return (
    <Card title="Your details">
      <div className="divide-y divide-hairline">
        <Row label="Email">{user.email}</Row>
        <Row label="Role">{user.isOwner ? "Owner" : titleCase(user.role)}</Row>
      </div>
      <p className="px-4 pb-4 pt-3 text-xs text-dim">
        Need the address changed? Ask an admin — it is also your sign-in, and on a marketer or
        customer account it has to move on both records at once.
      </p>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-3">
      <span className="field-label">{label}</span>
      <span className="text-sm text-ink">{children}</span>
    </div>
  );
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Changing a password ends every session, including this one — the API clears
 * the cookie on success. So this says so first and then sends the visitor to
 * sign in again, rather than letting the next click fail mysteriously.
 */
function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      api("/api/v1/auth/password", {
        method: "POST",
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      // The session is gone server-side; a full reload lands on sign-in
      // rather than leaving a shell whose every request now 401s.
      window.location.assign("/");
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (next !== confirm) {
      setLocalError("The two new passwords do not match.");
      return;
    }
    if (next.length < 12) {
      setLocalError("Use at least 12 characters.");
      return;
    }
    change.mutate();
  }

  return (
    <Card title="Password">
      <form className="space-y-4 p-4" onSubmit={submit}>
        <p className="text-sm text-muted">
          Changing this signs you out everywhere, including here. You will sign back in with the
          new password.
        </p>

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Current password">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              required
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label="New password" hint="At least 12 characters.">
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              required
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>
          <Field label="Repeat new password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              required
              onChange={(event) => setConfirm(event.target.value)}
            />
          </Field>
        </div>

        {localError !== null && <ErrorNote>{localError}</ErrorNote>}
        {change.isError && <ErrorNote>{messageFor(change.error)}</ErrorNote>}

        <Button type="submit" variant="primary" disabled={change.isPending}>
          {change.isPending ? "Changing…" : "Change password"}
        </Button>
      </form>
    </Card>
  );
}

function TwoFactor({ user }: { user: CurrentUser }) {
  return user.totpEnrolled ? <TurnOff /> : <TurnOn />;
}

function TurnOn() {
  const { setUser } = useAuth();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState("");

  const start = useMutation({
    mutationFn: () => api<Enrolment>("/api/v1/auth/totp/setup", { method: "POST" }),
    onSuccess: setEnrolment,
  });

  const enable = useMutation({
    mutationFn: () =>
      api<{ user: CurrentUser }>("/api/v1/auth/totp/enable", {
        method: "POST",
        body: { code },
      }),
    onSuccess: (result) => setUser(result.user),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    enable.mutate();
  }

  if (enrolment === null) {
    return (
      <Card
        title="Two-factor authentication"
        action={<Badge tone="quiet">Off</Badge>}
      >
        <div className="space-y-4 p-4">
          <p className="max-w-prose text-sm text-muted">
            Sign in with your password alone today. Turn this on and you will also enter a
            six-digit code from your phone each time.
          </p>
          {start.isError && <ErrorNote>{messageFor(start.error)}</ErrorNote>}
          <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? "Preparing…" : "Set up two-factor"}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Two-factor authentication" action={<Badge tone="accent">Setting up</Badge>}>
      <form onSubmit={submit} className="space-y-5 p-4">
        <ol className="space-y-4 text-sm text-muted">
          <li>
            <span className="text-ink">1.</span> Open Google Authenticator and tap the plus button,
            then <span className="text-ink">Scan a QR code</span>.
          </li>
          <li className="space-y-3">
            <span>
              <span className="text-ink">2.</span> Point the camera at this.
            </span>
            <QrCode value={enrolment.otpauthUrl} label="Two-factor setup code" />
            {/* A camera that will not focus, a phone on the other desk, a
                screen being shared — the typed key covers all of it. */}
            <details className="max-w-prose">
              <summary className="cursor-pointer text-sm text-accent">
                Can't scan it?
              </summary>
              <div className="mt-2 space-y-2">
                <p>
                  In the app choose <span className="text-ink">Enter a setup key</span>, use your
                  email address as the account name, leave the type as time-based, and enter:
                </p>
                <code className="block break-all rounded border border-hairline bg-base px-3 py-2 tabular text-ink">
                  {enrolment.secret}
                </code>
              </div>
            </details>
          </li>
          <li>
            <span className="text-ink">3.</span> Enter the six-digit code it shows.
          </li>
        </ol>

        <Field label="Six digit code" hint="It changes every 30 seconds.">
          <Input
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            className="tabular max-w-[12rem] text-lg tracking-[0.4em]"
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
          />
        </Field>

        {enable.isError && <ErrorNote>{messageFor(enable.error)}</ErrorNote>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={enable.isPending || code.length !== 6}>
            {enable.isPending ? "Checking…" : "Turn on two-factor"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setEnrolment(null);
              setCode("");
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TurnOff() {
  const { setUser } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");

  const disable = useMutation({
    mutationFn: () =>
      api<{ user: CurrentUser }>("/api/v1/auth/totp/disable", {
        method: "POST",
        body: { password },
      }),
    onSuccess: (result) => {
      setPassword("");
      setConfirming(false);
      setUser(result.user);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    disable.mutate();
  }

  return (
    <Card title="Two-factor authentication" action={<Badge tone="live">On</Badge>}>
      <div className="space-y-4 p-4">
        <p className="max-w-prose text-sm text-muted">
          Signing in asks for a code from your authenticator app as well as your password.
        </p>

        {confirming ? (
          <form onSubmit={submit} className="max-w-sm space-y-4">
            <Field
              label="Your password"
              hint="Confirms it is you, not a session someone else is holding."
            >
              <Input
                type="password"
                value={password}
                autoComplete="current-password"
                required
                autoFocus
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {disable.isError && <ErrorNote>{messageFor(disable.error)}</ErrorNote>}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="danger" disabled={disable.isPending}>
                {disable.isPending ? "Turning off…" : "Turn off two-factor"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  setPassword("");
                }}
              >
                Keep it on
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              Turn off two-factor
            </Button>
            {/* Said before they act, not after. Moving to a new phone is the
                usual reason to be on this screen, and doing it in the wrong
                order is what strands people. */}
            <p className="max-w-prose text-xs text-dim">
              Changing phones? Turn it off here first, then set it up again on the new device.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Something went wrong. Try again.";
}
